import { Contract, Coin, TxnStdFee } from "./contract";
import { SecretNetworkClient } from "secretjs";

export type ExecuteMsg = {
  receive: Snip20ReceiveMsg;
} | {
  execute_swap_operations: {
    max_spread?: Decimal | null;
    minimum_receive?: Uint128 | null;
    operations: SwapOperation[];
    to?: string | null;
    [k: string]: unknown;
  };
} | {
  execute_swap_operation: {
    max_spread?: Decimal | null;
    operation: SwapOperation;
    to?: string | null;
    [k: string]: unknown;
  };
} | {
  assert_minimum_receive: {
    asset_info: AssetInfo;
    minimum_receive: Uint128;
    prev_balance: Uint128;
    receiver: string;
    [k: string]: unknown;
  };
};
export type Uint128 = string;
export type Addr = string;
export type Binary = string;
export type Decimal = string;
export type SwapOperation = {
  native_swap: {
    ask_denom: string;
    offer_denom: string;
    [k: string]: unknown;
  };
} | {
  astro_swap: {
    ask_asset_info: AssetInfo;
    offer_asset_info: AssetInfo;
    [k: string]: unknown;
  };
};
export interface Snip20ReceiveMsg {
  amount: Uint128;
  from: Addr;
  memo?: string | null;
  msg?: Binary | null;
  sender: Addr;
  [k: string]: unknown;
}
export interface AssetInfo {
  contract_addr?: Addr | null;
  contract_code_hash?: string | null;
  denom?: string | null;
  [k: string]: unknown;
}
export interface InstantiateMsg {
  astroport_factory: string;
  factory_hash: string;
  [k: string]: unknown;
}
export type QueryMsg = {
  config: {
    [k: string]: unknown;
  };
} | {
  simulate_swap_operations: {
    offer_amount: Uint128;
    operations: SwapOperation[];
    [k: string]: unknown;
  };
};
export interface RouterReadOnlyInterface {
  config: () => Promise<any>;
  simulateSwapOperations: ({
    offerAmount,
    operations
  }: {
    offerAmount: Uint128;
    operations: SwapOperation[];
  }) => Promise<any>;
}
export class RouterQueryContract extends Contract implements RouterReadOnlyInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.config = this.config.bind(this);
    this.simulateSwapOperations = this.simulateSwapOperations.bind(this);
  }

  config = async (): Promise<any> => {
    return this.queryMsg({
      config: {}
    });
  };
  simulateSwapOperations = async ({
    offerAmount,
    operations
  }: {
    offerAmount: Uint128;
    operations: SwapOperation[];
  }): Promise<any> => {
    return this.queryMsg({
      simulate_swap_operations: {
        offer_amount: offerAmount,
        operations
      }
    });
  };
}
export interface RouterInterface extends RouterReadOnlyInterface {
  receive: ({
    userAddress,
    customFees,
    memo1,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo1?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount,
    from,
    memo,
    msg,
    sender
  }: {
    amount: string;
    from: string;
    memo?: string;
    msg?: Binary;
    sender: string;
  }) => Promise<any>;
  executeSwapOperations: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    maxSpread,
    minimumReceive,
    operations,
    to
  }: {
    maxSpread?: Decimal;
    minimumReceive?: Uint128;
    operations: SwapOperation[];
    to?: string;
  }) => Promise<any>;
  executeSwapOperation: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    maxSpread,
    operation,
    to
  }: {
    maxSpread?: Decimal;
    operation: object;
    to?: string;
  }) => Promise<any>;
  assertMinimumReceive: ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assetInfo,
    minimumReceive,
    prevBalance,
    receiver
  }: {
    assetInfo: AssetInfo;
    minimumReceive: string;
    prevBalance: string;
    receiver: string;
  }) => Promise<any>;
}
export class RouterContract extends RouterQueryContract implements RouterInterface {
  constructor(client: SecretNetworkClient, contractAddress: string, contractHash?: string) {
    super(client, contractAddress, contractHash);
    this.receive = this.receive.bind(this);
    this.executeSwapOperations = this.executeSwapOperations.bind(this);
    this.executeSwapOperation = this.executeSwapOperation.bind(this);
    this.assertMinimumReceive = this.assertMinimumReceive.bind(this);
  }

  receive = async ({
    userAddress,
    customFees,
    memo1,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo1?: string;
    transferAmount?: readonly Coin[];
  }, {
    amount,
    from,
    memo,
    msg,
    sender
  }: {
    amount: string;
    from: string;
    memo?: string;
    msg?: Binary;
    sender: string;
  }): Promise<any> => {
    return await this.executeMsg({
      receive: {
        amount,
        from,
        memo,
        msg,
        sender
      }
    }, userAddress, customFees, memo1, transferAmount);
  };
  executeSwapOperations = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    maxSpread,
    minimumReceive,
    operations,
    to
  }: {
    maxSpread?: Decimal;
    minimumReceive?: Uint128;
    operations: SwapOperation[];
    to?: string;
  }): Promise<any> => {
    return await this.executeMsg({
      execute_swap_operations: {
        max_spread: maxSpread,
        minimum_receive: minimumReceive,
        operations,
        to
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  executeSwapOperation = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    maxSpread,
    operation,
    to
  }: {
    maxSpread?: Decimal;
    operation: object;
    to?: string;
  }): Promise<any> => {
    return await this.executeMsg({
      execute_swap_operation: {
        max_spread: maxSpread,
        operation,
        to
      }
    }, userAddress, customFees, memo, transferAmount);
  };
  assertMinimumReceive = async ({
    userAddress,
    customFees,
    memo,
    transferAmount
  }: {
    userAddress: string;
    customFees?: TxnStdFee;
    memo?: string;
    transferAmount?: readonly Coin[];
  }, {
    assetInfo,
    minimumReceive,
    prevBalance,
    receiver
  }: {
    assetInfo: AssetInfo;
    minimumReceive: string;
    prevBalance: string;
    receiver: string;
  }): Promise<any> => {
    return await this.executeMsg({
      assert_minimum_receive: {
        asset_info: assetInfo,
        minimum_receive: minimumReceive,
        prev_balance: prevBalance,
        receiver
      }
    }, userAddress, customFees, memo, transferAmount);
  };
}